/**
 * Ce que le studio revend — boissons et accessoires de cheveux — dans une
 * seule table plate (pas de sous-catégories), et la trace
 * de toutes les entrées de stock.
 *
 * Le stock ne se saisit jamais directement — il monte par un approvisionnement
 * et descend à la vente. C'est ce qui rend l'historique fiable : chaque unité
 * en stock vient d'une ligne d'achat datée.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Card, CardContent, Typography, TextField, Button, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert,
  Tooltip, Tabs, Tab, Switch, FormControlLabel, InputAdornment, MenuItem,
} from '@mui/material';
import {
  Edit, Delete, Add, Search, LocalCafe, Inventory, History, Warning, Checkroom,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import {
  articleStudioAPI, approvisionnementAPI, extraireListe,
} from '../../../services/studioAPI';

const htg = (valeur) => `${Number(valeur || 0).toLocaleString()} HTG`;

const dateHeure = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
};

const TYPES_ARTICLE = [
  { value: 'boisson', label: 'Boisson' },
  { value: 'accessoire', label: 'Accessoire de cheveux' },
];

const ARTICLE_VIDE = {
  nom: '', type_article: 'boisson',
  prix: '', cout_revient: '', seuil_alerte: 5, est_active: true,
  // Uniquement à la création : sert à générer la première entrée de stock
  stock_initial: '',
};
const ENTREE_VIDE = { article: '', quantite: '', cout_unitaire: '', fournisseur: '', notes: '' };

const Articles = () => {
  const [onglet, setOnglet] = useState(0);
  const [articles, setArticles] = useState([]);
  const [entrees, setEntrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [filtreType, setFiltreType] = useState('');
  const [filtreArticle, setFiltreArticle] = useState('');

  const [dialogArticle, setDialogArticle] = useState(false);
  const [dialogEntree, setDialogEntree] = useState(false);
  const [enEdition, setEnEdition] = useState(false);
  const [formArticle, setFormArticle] = useState(ARTICLE_VIDE);
  const [formEntree, setFormEntree] = useState(ENTREE_VIDE);

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      setErreur(null);
      const params = filtreArticle ? { article: filtreArticle } : {};
      const [reponseArticles, reponseEntrees] = await Promise.all([
        articleStudioAPI.getAll(),
        approvisionnementAPI.getAll(params),
      ]);
      setArticles(extraireListe(reponseArticles));
      setEntrees(extraireListe(reponseEntrees));
    } catch (error) {
      const message = error.response?.status === 403
        ? "Les entrées de stock sont réservées au propriétaire et au manager"
        : (error.response?.data?.detail || error.message || 'Erreur lors du chargement');
      setErreur(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [filtreArticle]);

  useEffect(() => { charger(); }, [charger]);

  const affichees = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    return articles.filter((article) => {
      const correspond = !terme || article.nom.toLowerCase().includes(terme);
      const bonType = !filtreType || article.type_article === filtreType;
      return correspond && bonType;
    });
  }, [articles, recherche, filtreType]);

  const aRacheter = articles.filter((article) => article.est_active && article.stock_faible);
  const valeurStock = articles.reduce(
    (somme, article) => somme + Number(article.stock || 0) * Number(article.cout_revient || 0),
    0
  );

  // --- Articles ---

  const ouvrirDialogArticle = (article = null) => {
    if (article) {
      setFormArticle({
        id: article.id,
        nom: article.nom,
        type_article: article.type_article,
        prix: article.prix,
        cout_revient: article.cout_revient,
        seuil_alerte: article.seuil_alerte,
        est_active: article.est_active,
        stock_initial: '',
      });
      setEnEdition(true);
    } else {
      setFormArticle(ARTICLE_VIDE);
      setEnEdition(false);
    }
    setDialogArticle(true);
  };

  const enregistrerArticle = async () => {
    if (!formArticle.nom.trim()) return toast.error('Le nom est obligatoire');
    if (formArticle.prix === '' || Number(formArticle.prix) < 0) {
      return toast.error('Saisissez un prix de vente valide');
    }

    try {
      const { stock_initial: stockInitial, ...champs } = formArticle;
      const donnees = {
        ...champs,
        prix: Number(formArticle.prix),
        cout_revient: Number(formArticle.cout_revient || 0),
        seuil_alerte: Number(formArticle.seuil_alerte || 0),
      };

      if (enEdition) {
        await articleStudioAPI.update(formArticle.id, donnees);
        toast.success('Article modifié');
      } else {
        const creee = await articleStudioAPI.create(donnees);

        // Un article à zéro n'apparaît pas à la caisse (le catalogue ne sert
        // que ce qui est en stock) : on enchaîne sur la première entrée pour
        // qu'elle soit vendable tout de suite.
        const quantite = Number(stockInitial || 0);
        if (quantite > 0) {
          await approvisionnementAPI.create({
            article: creee.data.id,
            quantite,
            cout_unitaire: Number(formArticle.cout_revient || 0),
            notes: 'Stock initial à la création',
          });
          toast.success(`Article créé avec ${quantite} en stock`);
        } else {
          toast.success('Article créé — ajoutez du stock pour le rendre vendable');
        }
      }
      setDialogArticle(false);
      charger();
    } catch (error) {
      toast.error(
        error.response?.data?.nom?.[0] || error.response?.data?.detail
        || "Erreur lors de l'enregistrement"
      );
    }
  };

  const basculerStatut = async (article) => {
    try {
      await articleStudioAPI.patch(article.id, { est_active: !article.est_active });
      toast.success(article.est_active ? 'Article retiré de la carte' : 'Article remis');
      charger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de statut');
    }
  };

  const supprimerArticle = async (article) => {
    if (!window.confirm(`Supprimer « ${article.nom} » ?`)) return;
    try {
      await articleStudioAPI.delete(article.id);
      toast.success('Article supprimé');
      charger();
    } catch (error) {
      toast.error('Impossible de supprimer un article déjà vendu — désactivez-le plutôt');
    }
  };

  // --- Entrées de stock ---

  const ouvrirDialogEntree = (article = null) => {
    setFormEntree({
      ...ENTREE_VIDE,
      article: article?.id || '',
      // Pré-rempli au dernier prix connu : le plus souvent il n'a pas bougé
      cout_unitaire: article ? String(Number(article.cout_revient || 0)) : '',
    });
    setDialogEntree(true);
  };

  const enregistrerEntree = async () => {
    if (!formEntree.article) return toast.error('Choisissez un article');
    if (!formEntree.quantite || Number(formEntree.quantite) <= 0) {
      return toast.error('Saisissez une quantité supérieure à zéro');
    }
    if (formEntree.cout_unitaire === '' || Number(formEntree.cout_unitaire) < 0) {
      return toast.error("Saisissez le prix d'achat unitaire");
    }

    try {
      await approvisionnementAPI.create({
        ...formEntree,
        quantite: Number(formEntree.quantite),
        cout_unitaire: Number(formEntree.cout_unitaire),
      });
      toast.success('Entrée de stock enregistrée');
      setDialogEntree(false);
      charger();
    } catch (error) {
      toast.error(
        error.response?.data?.quantite?.[0] || error.response?.data?.detail
        || "Erreur lors de l'enregistrement"
      );
    }
  };

  const coutEntree = Number(formEntree.quantite || 0) * Number(formEntree.cout_unitaire || 0);

  if (loading && articles.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Boissons &amp; accessoires</Typography>
          <Typography variant="body2" color="text.secondary">
            Ce qu'on vend au salon, et d'où vient le stock
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Inventory />}
            onClick={() => ouvrirDialogEntree()}
            disabled={articles.length === 0}
          >
            Entrée de stock
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => ouvrirDialogArticle()}>
            Nouvel article
          </Button>
        </Box>
      </Box>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
      {aRacheter.length > 0 && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
          À réapprovisionner : {aRacheter.map((article) => article.nom).join(', ')}
        </Alert>
      )}

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Références</Typography>
            <Typography variant="h4" fontWeight="bold">{articles.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Unités en stock</Typography>
            <Typography variant="h4" fontWeight="bold">
              {articles.reduce((somme, article) => somme + Number(article.stock || 0), 0)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Valeur du stock</Typography>
            <Typography variant="h4" fontWeight="bold">{htg(valeurStock)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">À racheter</Typography>
            <Typography
              variant="h4"
              fontWeight="bold"
              color={aRacheter.length ? 'error.main' : 'text.primary'}
            >
              {aRacheter.length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Tabs value={onglet} onChange={(e, valeur) => setOnglet(valeur)} sx={{ mb: 2 }}>
        <Tab label="Stock" icon={<LocalCafe />} iconPosition="start" />
        <Tab label={`Ajouts (${entrees.length})`} icon={<History />} iconPosition="start" />
      </Tabs>

      {onglet === 0 ? (
        <>
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <TextField
              size="small" placeholder="Chercher un article" value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              sx={{ flex: '1 1 260px', maxWidth: 320 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
              }}
            />
            <TextField
              size="small" select label="Genre" value={filtreType}
              onChange={(e) => setFiltreType(e.target.value)}
              sx={{ minWidth: 210 }}
            >
              <MenuItem value="">Tous</MenuItem>
              {TYPES_ARTICLE.map((type) => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </TextField>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Article</TableCell>
                  <TableCell>Genre</TableCell>
                  <TableCell align="right">Prix de vente</TableCell>
                  <TableCell align="right">Prix d'achat</TableCell>
                  <TableCell align="right">Marge</TableCell>
                  <TableCell align="center">Stock</TableCell>
                  <TableCell align="center">Statut</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {affichees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <LocalCafe sx={{ fontSize: 48, color: 'text.disabled' }} />
                      <Typography color="text.secondary" mt={1}>
                        Aucun article enregistré
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : affichees.map((article) => (
                  <TableRow key={article.id} hover>
                    <TableCell><Typography fontWeight={600}>{article.nom}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={article.type_article === 'accessoire' ? <Checkroom /> : <LocalCafe />}
                        label={article.type_article_display || article.type_article}
                      />
                    </TableCell>
                    <TableCell align="right">{htg(article.prix)}</TableCell>
                    <TableCell align="right">{htg(article.cout_revient)}</TableCell>
                    <TableCell align="right">
                      <Typography color="success.main" fontWeight={600}>
                        {htg(article.marge_unitaire)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={article.stock}
                        color={article.stock_faible ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={article.est_active}
                            onChange={() => basculerStatut(article)}
                          />
                        }
                        label=""
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Ajouter du stock">
                        <IconButton size="small" onClick={() => ouvrirDialogEntree(article)}>
                          <Inventory />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => ouvrirDialogArticle(article)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => supprimerArticle(article)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <>
          <Box mb={2}>
            <TextField
              size="small" select label="Article" value={filtreArticle}
              onChange={(e) => setFiltreArticle(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Toutes</MenuItem>
              {articles.map((article) => (
                <MenuItem key={article.id} value={article.id}>{article.nom}</MenuItem>
              ))}
            </TextField>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Article</TableCell>
                  <TableCell align="center">Quantité</TableCell>
                  <TableCell align="right">Prix d'achat</TableCell>
                  <TableCell align="right">Coût total</TableCell>
                  <TableCell align="center">Stock</TableCell>
                  <TableCell>Fournisseur</TableCell>
                  <TableCell>Par</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entrees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <History sx={{ fontSize: 48, color: 'text.disabled' }} />
                      <Typography color="text.secondary" mt={1}>
                        Aucune entrée de stock
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : entrees.map((entree) => (
                  <TableRow key={entree.id} hover>
                    <TableCell>{dateHeure(entree.date)}</TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>{entree.article_nom}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" color="success" label={`+${entree.quantite}`} />
                    </TableCell>
                    <TableCell align="right">{htg(entree.cout_unitaire)}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600}>{htg(entree.cout_total)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">
                        {entree.stock_avant} → {entree.stock_apres}
                      </Typography>
                    </TableCell>
                    <TableCell>{entree.fournisseur || '—'}</TableCell>
                    <TableCell>{entree.enregistre_par_nom || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Alert severity="info" sx={{ mt: 2 }}>
            L'historique n'est ni modifiable ni supprimable : chaque entrée a déjà bougé
            le stock. Pour corriger un lot, enregistrez une nouvelle entrée.
          </Alert>
        </>
      )}

      {/* Dialogue article */}
      <Dialog open={dialogArticle} onClose={() => setDialogArticle(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{enEdition ? "Modifier l'article" : 'Nouvel article'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Nom" fullWidth required autoFocus
              value={formArticle.nom}
              onChange={(e) => setFormArticle({ ...formArticle, nom: e.target.value })}
              placeholder="Coca-Cola, Perruque naturelle, Mèches..."
            />
            <TextField
              label="Genre" select fullWidth required
              value={formArticle.type_article}
              onChange={(e) => setFormArticle({ ...formArticle, type_article: e.target.value })}
              helperText="Décide de l'onglet dans lequel l'article apparaît à la caisse"
            >
              {TYPES_ARTICLE.map((type) => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </TextField>
            <Box display="flex" gap={2}>
              <TextField
                label="Prix de vente" type="number" fullWidth required
                value={formArticle.prix}
                onChange={(e) => setFormArticle({ ...formArticle, prix: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">HTG</InputAdornment> }}
              />
              <TextField
                label="Prix d'achat" type="number" fullWidth
                value={formArticle.cout_revient}
                onChange={(e) => setFormArticle({ ...formArticle, cout_revient: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">HTG</InputAdornment> }}
                helperText="Mis à jour automatiquement à chaque entrée de stock"
              />
            </Box>
            <TextField
              label="Seuil d'alerte" type="number" fullWidth
              value={formArticle.seuil_alerte}
              onChange={(e) => setFormArticle({ ...formArticle, seuil_alerte: e.target.value })}
              helperText="En dessous de ce nombre, l'article est signalé à racheter"
            />
            {!enEdition && (
              <>
                <TextField
                  label="Stock initial" type="number" fullWidth
                  value={formArticle.stock_initial}
                  onChange={(e) => setFormArticle({ ...formArticle, stock_initial: e.target.value })}
                  helperText="Enregistré comme première entrée de stock, au prix d'achat ci-dessus"
                />
                <Alert severity="info">
                  Un article à zéro n'apparaît pas à la caisse : le catalogue ne
                  propose que ce qui est en stock. Laissez vide si vous préférez
                  saisir l'entrée plus tard.
                </Alert>
              </>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={formArticle.est_active}
                  onChange={(e) => setFormArticle({ ...formArticle, est_active: e.target.checked })}
                />
              }
              label="Proposée à la caisse"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogArticle(false)}>Annuler</Button>
          <Button variant="contained" onClick={enregistrerArticle}>
            {enEdition ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue entrée de stock */}
      <Dialog open={dialogEntree} onClose={() => setDialogEntree(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Entrée de stock</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Article" select fullWidth required
              value={formEntree.article}
              onChange={(e) => {
                const choisi = articles.find((a) => a.id === e.target.value);
                setFormEntree({
                  ...formEntree,
                  article: e.target.value,
                  cout_unitaire: choisi ? String(Number(choisi.cout_revient || 0)) : '',
                });
              }}
            >
              {articles.map((article) => (
                <MenuItem key={article.id} value={article.id}>
                  {article.nom} — {article.stock} en stock
                </MenuItem>
              ))}
            </TextField>
            <Box display="flex" gap={2}>
              <TextField
                label="Quantité" type="number" fullWidth required
                value={formEntree.quantite}
                onChange={(e) => setFormEntree({ ...formEntree, quantite: e.target.value })}
              />
              <TextField
                label="Prix d'achat unitaire" type="number" fullWidth required
                value={formEntree.cout_unitaire}
                onChange={(e) => setFormEntree({ ...formEntree, cout_unitaire: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">HTG</InputAdornment> }}
              />
            </Box>
            {coutEntree > 0 && (
              <Typography align="right" fontWeight={600}>
                Coût de l'achat : {htg(coutEntree)}
              </Typography>
            )}
            <TextField
              label="Fournisseur" fullWidth
              value={formEntree.fournisseur}
              onChange={(e) => setFormEntree({ ...formEntree, fournisseur: e.target.value })}
            />
            <TextField
              label="Notes" fullWidth multiline rows={2}
              value={formEntree.notes}
              onChange={(e) => setFormEntree({ ...formEntree, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogEntree(false)}>Annuler</Button>
          <Button variant="contained" onClick={enregistrerEntree}>Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Articles;
